import { isSalesTaxJurisdiction } from "../lib/taxedLineRateIds.js";
// Localized tax name abbreviations by jurisdiction code.
// US states are handled by the SALES_TAX fallback below.
export const TAX_NAME_BY_JURISDICTION = {
    NL: 'BTW', BE: 'BTW',
    LU: 'TVA', FR: 'TVA',
    DE: 'USt', AT: 'USt',
    IT: 'IVA', ES: 'IVA', PT: 'IVA',
    IE: 'VAT', FI: 'ALV', SE: 'Moms', DK: 'Moms',
    PL: 'PTU', CZ: 'DPH', HU: 'ÁFA', SK: 'DPH',
    SI: 'DDV', EE: 'KM', LV: 'PVN', LT: 'PVM',
    RO: 'TVA', BG: 'ДДС', HR: 'PDV',
    CY: 'ΦΠΑ', MT: 'VAT', GR: 'ΦΠΑ',
};
/**
 * Localized tax abbreviation for a jurisdiction code.
 *
 * Exactly the fallback chain `useJurisdictionTaxConfig` applies (and
 * `getJurisdictionLabel` duplicates): US codes (`US-VT`, or a bare `US`
 * country — `isSalesTaxJurisdiction`) are sales-tax jurisdictions, a table hit wins next, and everything else — including no
 * jurisdiction at all, which is what the hook's unconfigured branch returns —
 * falls back to the generic "VAT".
 */
export function resolveTaxName(jurisdiction) {
    if (!jurisdiction)
        return 'VAT';
    return isSalesTaxJurisdiction(jurisdiction)
        ? 'Sales Tax'
        : (TAX_NAME_BY_JURISDICTION[jurisdiction] ?? 'VAT');
}
/**
 * Pure jurisdiction precedence: the saved tax setting is authoritative, the
 * org's country is the fallback, and a blank (empty or whitespace) value
 * counts as unset; the result is trimmed. The twin of the SQL's
 * `_org_tax_jurisdiction` (20261101130000), which returns `''` where this
 * returns null.
 */
export function resolveTaxJurisdictionFrom(primaryTaxJurisdiction, orgCountry) {
    return primaryTaxJurisdiction?.trim() || orgCountry?.trim() || null;
}
/**
 * Resolve an org's tax jurisdiction: `organization_tax_settings.primary_tax_jurisdiction`
 * first, then `organizations.country`.
 *
 * Mirrors `assertNlJurisdiction` in `apps/api/src/routes/accounting/nl/wkr.ts` minus its
 * NL-only throw. Query errors still surface (an RLS denial must not silently
 * read as "no jurisdiction"); a missing row does not — neither table is
 * guaranteed to be populated.
 */
export async function resolveTaxJurisdiction(client, orgId) {
    const [settings, org] = await Promise.all([
        client
            .from("organization_tax_settings")
            .select("primary_tax_jurisdiction")
            .eq("organization_id", orgId)
            .maybeSingle(),
        client.from("organizations").select("country").eq("id", orgId).maybeSingle(),
    ]);
    if (settings.error)
        throw settings.error;
    if (org.error)
        throw org.error;
    return resolveTaxJurisdictionFrom(settings.data?.primary_tax_jurisdiction, org.data?.country);
}
//# sourceMappingURL=taxIdentity.js.map